import './HorizontalScrollable.less';
import IconButton from '../button/IconButton';
import { type PropsWithChildren } from 'preact/compat';
import { useRef, useState, useEffect } from 'preact/hooks';

export type HorizontalScrollableProps = PropsWithChildren<{
    /**
     * The distance scrolled by each click, in pixels.
     */
    stepDistance?: number;
    /**
     * Whether the scroll buttons are hidden
     */
    hiddenButtons?: boolean;
}>;

const HorizontalScrollable = ({
    children,
    stepDistance = 200,
    hiddenButtons
}: HorizontalScrollableProps) => {
    const horizontalContainerRef = useRef<HTMLDivElement>(null);
    const [isLeftArrowHidden, setLeftArrowHidden] = useState(true);
    const [isRightArrowHidden, setRightArrowHidden] = useState(true);

    useEffect(() => {
        if (!horizontalContainerRef.current) {
            return;
        }
        const { current } = horizontalContainerRef;
        const resizeListener = () => {
            setLeftArrowHidden(hiddenButtons || current.scrollLeft <= 0);
            setRightArrowHidden(
                hiddenButtons ||
                    current.scrollLeft + current.offsetWidth >=
                        current.scrollWidth
            );
        };
        resizeListener();
        window.addEventListener('resize', resizeListener);
        const observer = new MutationObserver(resizeListener);
        observer.observe(current, { childList: true, subtree: true });

        return () => {
            window.removeEventListener('resize', resizeListener);
            observer.disconnect();
        };
    }, [horizontalContainerRef.current, hiddenButtons]);

    const leftScroll = (): void => {
        if (!horizontalContainerRef.current) {
            return;
        }
        const { current } = horizontalContainerRef;
        setRightArrowHidden(false);
        setLeftArrowHidden(current.scrollLeft - stepDistance <= 0);
        current.scrollBy(-stepDistance, 0);
    };

    const rightScroll = (): void => {
        if (!horizontalContainerRef.current) {
            return;
        }
        const { current } = horizontalContainerRef;
        setLeftArrowHidden(false);
        setRightArrowHidden(
            current.scrollLeft + stepDistance + current.offsetWidth >=
                current.scrollWidth
        );
        current.scrollBy(stepDistance, 0);
    };

    return (
        <div style={{ position: 'relative' }}>
            <div
                ref={horizontalContainerRef}
                className="bri-HorizontalScrollable-content"
            >
                {children}
            </div>
            <IconButton
                variant="light"
                className={`bri-HorizontalScrollable-left-button ${
                    !isLeftArrowHidden ? 'bri-visible' : ''
                }`}
                iconId="chevron-left"
                onClick={() => leftScroll()}
            />
            <IconButton
                variant="light"
                iconId="chevron-right"
                className={`bri-HorizontalScrollable-right-button ${
                    !isRightArrowHidden ? 'bri-visible' : ''
                }`}
                onClick={() => rightScroll()}
            />
        </div>
    );
};

export default HorizontalScrollable;
